import { type AuthStateType } from '@/shared/authentication/utils/constants';
import { isAuthorized, type AllowEntry } from '@/shared/authorization/isAuthorized';
import type { FeatureFlagName } from '@/shared/hooks/use-feature-flags';

import type { IconName } from '@bozic/ui/icon';
import type { RouteObject } from 'react-router-dom';

export interface NavGateContext {
  role: AuthStateType;
  flags: Record<FeatureFlagName, boolean>;
  isLiaFcs: boolean;
}

/** Role allow-list (resolved via isAuthorized) or a predicate over flags/tenant. */
export type NavGate = readonly AllowEntry[] | ((ctx: NavGateContext) => boolean);

/**
 * Nav metadata carried on a route's `handle` (spec §3): the single source the
 * sidebar tree, breadcrumb, and section index redirects all derive from. A
 * route without a label is routable but never appears in navigation.
 */
export interface NavHandle {
  label: string;
  icon?: IconName;
  gate?: NavGate;
  /** Renders in the tree but inert, keyed off route params (e.g. LGM Endorsements for swine). */
  disabledWhen?: (params: Record<string, string | undefined>) => boolean;
}

export interface NavTreeNode {
  /** Path relative to the routes passed in, e.g. `overview/preferences`. */
  path: string;
  label: string;
  icon?: IconName;
  disabled?: boolean;
  children?: NavTreeNode[];
}

const passesGate = (gate: NavGate | undefined, ctx: NavGateContext): boolean =>
  !gate || (typeof gate === 'function' ? gate(ctx) : isAuthorized(ctx.role, gate));

const buildNode = (
  handle: NavHandle,
  { path, children }: { path: string; children: NavTreeNode[] },
  params: Record<string, string | undefined>
): NavTreeNode => ({
  path,
  label: handle.label,
  ...(handle.icon ? { icon: handle.icon } : {}),
  ...(handle.disabledWhen?.(params) ? { disabled: true } : {}),
  ...(children.length ? { children } : {})
});

/**
 * Derive the navigation tree from (already tenant-pruned) route children:
 * labeled routes that pass their gate, nested as in the route config. Pass a
 * program root's `children` (or the admin route's) to get its section tree.
 */
export const deriveNavTree = (
  routes: readonly RouteObject[],
  ctx: NavGateContext,
  base = '',
  params: Record<string, string | undefined> = {}
  // eslint-disable-next-line max-params -- base and params both thread through the recursion
): NavTreeNode[] =>
  routes.flatMap(route => {
    const handle = route.handle as NavHandle | undefined;
    if (!route.path || !handle?.label || !passesGate(handle.gate, ctx)) return [];
    const path = base ? `${base}/${route.path}` : route.path;
    const children = route.children ? deriveNavTree(route.children, ctx, path, params) : [];
    return [buildNode(handle, { path, children }, params)];
  });

/** Target for a section parent's index redirect: its first nav-visible child. */
export const firstVisibleChildPath = (
  routes: readonly RouteObject[],
  ctx: NavGateContext
): string | undefined => deriveNavTree(routes, ctx)[0]?.path;
